/**
 * "Neon Primer" — original interactive-object tutorial (difficulty 2/10).
 * Picks up where First Steps ends: floating text labels teach pads, orbs,
 * gravity portals, and mini mode, each in an isolated safe section.
 * Authored in block coordinates; produces standard SSL LevelData.
 */
import type { LevelData, LevelObject } from '../../format/types';
import { UNITS_PER_BLOCK as B } from '../../engine/physics/constants';

let nextId = 1;
const objects: LevelObject[] = [];

function cell(bx: number, by: number): { x: number; y: number } {
  return { x: bx * B + B / 2, y: by * B + B / 2 };
}

function push(o: Omit<LevelObject, 'id'>): void {
  objects.push({ id: nextId++, ...o });
}

const spike = (bx: number, by = 0, hanging = false): void =>
  push({ type: 'spike', ...cell(bx, by), ...(hanging ? { flipY: true } : {}) });
const block = (bx: number, by = 0): void => push({ type: 'block', ...cell(bx, by) });
const ceiling = (bx0: number, bx1: number, by: number): void => {
  for (let bx = bx0; bx <= bx1; bx++) block(bx, by);
};
const portal = (bx: number, kind: string, by = 1): void =>
  push({ type: 'portal', ...cell(bx, by), props: { kind } });
const pad = (bx: number, kind: string): void =>
  push({ type: 'pad', ...cell(bx, 0), props: { kind } });
const orb = (bx: number, by: number, kind: string): void =>
  push({ type: 'orb', ...cell(bx, by), props: { kind } });
const txt = (bx: number, by: number, text: string, size = 1): void =>
  push({ type: 'text', ...cell(bx, by), props: { text, size } });

// --- Act 1: warm-up jumps (0–48) ----------------------------------------------
txt(8, 4, 'NEON PRIMER', 1.6);
txt(22, 3, 'TAP TO JUMP');
spike(26);
spike(34);
spike(42);
spike(43); // one double, known from First Steps

// --- Act 2: pads (48–64) -------------------------------------------------------
txt(52, 3, 'PADS LAUNCH YOU');
pad(56, 'strong');
spike(57);
spike(58);
spike(59);

// --- Act 3: orbs (64–80) -------------------------------------------------------
txt(66, 3, 'TAP ORBS IN THE AIR');
orb(72, 1, 'strong');
spike(72);
spike(73);
spike(74);

// --- Act 4: gravity (80–114) ---------------------------------------------------
txt(80, 3, 'GRAVITY FLIPS AHEAD');
portal(86, 'gravity:up');
ceiling(84, 112, 5);
spike(94, 4, true); // jump "down" off the ceiling
spike(102, 4, true);
portal(110, 'gravity:down', 4);
portal(110, 'gravity:down', 1); // safety catch if already grounded

// --- Act 5: mini mode (114–142) ------------------------------------------------
txt(116, 3, 'MINI FITS ANYWHERE');
portal(120, 'size:mini');
ceiling(124, 134, 3); // low tunnel only mini clears comfortably
spike(129);
portal(138, 'size:normal');

// --- Finale (142–160) ----------------------------------------------------------
txt(146, 4, 'YOU ARE READY');
spike(150);
spike(151);

// Secret shards along deliberate arcs.
push({ type: 'collectible', ...cell(30, 2), props: { kind: 'shard' } });
push({ type: 'collectible', ...cell(67, 2), props: { kind: 'shard' } });
push({ type: 'collectible', ...cell(131, 1), props: { kind: 'shard' } });

push({ type: 'endgate', ...cell(160, 2) });

export const NEON_PRIMER: LevelData = {
  formatVersion: 1,
  meta: {
    name: 'Neon Primer',
    author: 'Shape Shifters Team',
    description: 'Pads, orbs, gravity, mini — school is in session.',
    revision: 1,
    createdAt: '2026-07-24T00:00:00.000Z',
    updatedAt: '2026-07-24T00:00:00.000Z',
    difficulty: 2,
    tags: ['tutorial', 'official'],
  },
  song: { id: 'neon-circuit', bpm: 128, offsetMs: 0 },
  settings: {
    mode: 'normal',
    speed: 'one',
    startForm: 'runner',
    accentColor: '#34d399',
    hazardColor: '#f43f5e',
    backgroundColor: '#071a14',
  },
  objects,
};
